import React, { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, ListChecks, Building2, Users, FileText,
  Search, Bell, Menu, Plus, LogOut, MessageSquare,
} from "lucide-react";
import { Input } from "./ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { toast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { fetchNotifications, markNotificationsRead } from "../api";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/tasks", label: "Tasks", icon: ListChecks },
  { to: "/customers", label: "Customers", icon: Building2 },
  { to: "/team", label: "Team Members", icon: Users },
  { to: "/documents", label: "Documents", icon: FileText },
];

const pageTitles = {
  "/": "Dashboard", "/projects": "Projects", "/tasks": "Tasks",
  "/customers": "Customers", "/team": "Team Members", "/documents": "Documents",
};

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const loadNotifs = useCallback(() => {
    if (!user) return;
    fetchNotifications(user.id).then((d) => { setNotifs(d.items); setUnread(d.unread); }).catch(() => {});
  }, [user]);

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 15000);
    return () => clearInterval(t);
  }, [loadNotifs]);

  // Greet with unread count once on login
  useEffect(() => {
    if (!user) return;
    fetchNotifications(user.id).then((d) => {
      if (d.unread > 0) toast({ title: `Welcome back, ${user.name.split(" ")[0]}`, description: `You have ${d.unread} unread notification${d.unread > 1 ? "s" : ""}.` });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const openNotif = async (n) => {
    await markNotificationsRead(user.id, [n.id]);
    loadNotifs();
    navigate(n.scope === "project" ? `/projects/${n.refId}` : `/tasks?open=${n.refId}`);
  };

  const markAll = async () => { await markNotificationsRead(user.id); loadNotifs(); };

  const title = pageTitles[location.pathname] ||
    (location.pathname.startsWith("/projects/") ? "Project Detail" : "EP Group PMS");

  const Sidebar = (
    <aside className="flex h-full w-64 flex-col border-r border-slate-800 bg-[#282b36]">
      <div className="flex items-center gap-2.5 px-5 py-5">
        <img
          src="https://images.squarespace-cdn.com/content/v1/60bf46855b35197d90ad4d6a/2fe6a5e1-3577-4344-a25f-eb0a17310d8b/EPG50+-+landscape+over+red+background%404x.png?format=1500w"
          alt="EP Group"
          className="h-8 w-auto"
        />
      </div>

      <nav className="flex-1 space-y-1 px-3 py-2">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[#d0333a] text-white"
                  : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <item.icon className={`h-[18px] w-[18px] ${isActive ? "text-white" : "text-slate-400 group-hover:text-slate-200"}`} />
                {item.label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/10 p-4">
        <div className="flex items-center gap-3 rounded-lg bg-white/5 p-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: user?.color || "#d0333a" }}>{user?.initials || "EP"}</div>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-sm font-semibold text-white">{user?.name || "EP Group"}</p>
            <p className="truncate text-[11px] text-slate-400">{user?.role || "Administrator"}</p>
          </div>
          <button onClick={() => { logout(); navigate("/login"); }} title="Switch user" className="rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Desktop sidebar */}
      <div className="hidden md:block">{Sidebar}</div>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full animate-scale-in">{Sidebar}</div>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Topbar */}
        <header className="flex h-16 items-center gap-4 border-b border-slate-200 bg-white px-4 md:px-6">
          <button className="md:hidden" onClick={() => setMobileOpen(true)}>
            <Menu className="h-6 w-6 text-slate-600" />
          </button>
          <h1 className="text-lg font-bold text-slate-900">{title}</h1>

          <div className="relative ml-auto hidden max-w-xs flex-1 sm:block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search this app..."
              className="h-9 border-slate-200 bg-slate-50 pl-9 text-sm focus-visible:bg-white"
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex h-9 items-center gap-1.5 rounded-lg bg-red-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-red-700">
                <Plus className="h-4 w-4" /> <span className="hidden sm:inline">New</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Create new record</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {["Project", "Task", "Customer", "Team Member", "Document"].map((x) => (
                <DropdownMenuItem key={x} onClick={() => toast({ title: `Add a New ${x}`, description: "Connect the backend to save this record." })}>
                  Add a New {x}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100">
                <Bell className="h-5 w-5" />
                {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#d0333a] px-1 text-[10px] font-bold text-white">{unread}</span>}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between px-2 py-1.5">
                <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
                {unread > 0 && <button onClick={markAll} className="text-xs font-semibold text-[#d0333a] hover:underline">Mark all read</button>}
              </div>
              <DropdownMenuSeparator />
              {notifs.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-slate-400">You're all caught up.</div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  {notifs.slice(0, 20).map((n) => (
                    <button key={n.id} onClick={() => openNotif(n)}
                      className={`flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-slate-50 ${!n.read ? "bg-red-50/40" : ""}`}>
                      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{ backgroundColor: n.fromColor || "#64748b" }}>{n.fromInitials || "?"}</div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-slate-700"><span className="font-semibold">{n.fromUserName}</span> in <span className="font-medium">{n.refName}</span></p>
                        <p className="truncate text-xs text-slate-500">{n.preview}</p>
                        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400"><MessageSquare className="h-3 w-3" /> {timeAgo(n.ts)}</p>
                      </div>
                      {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#d0333a]" />}
                    </button>
                  ))}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white py-1 pl-2 pr-3">
            <span className="hidden text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:inline">Client</span>
            <span className="text-base font-extrabold italic tracking-tight text-[#0a2ea0]">Primark</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
